import { FC, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNotify } from "react-admin";
import { AxiosError, getSeeriApi } from "@/api";

type ClientType = "PERSON" | "COMPANY";

type FormValues = {
  type: ClientType;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  documentType: string;
  documentId?: string;
  email?: string;
  phonePrefix: string;
  phone?: string;
  observations?: string;
};

type ClientV2Response = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
};

const DOC_TYPES = [
  { id: "CC", name: "Cédula de ciudadanía" },
  { id: "CE", name: "Cédula de extranjería" },
  { id: "NIT", name: "NIT" },
  { id: "PA", name: "Pasaporte" },
  { id: "TI", name: "Tarjeta de identidad" },
];

const PHONE_PREFIXES = ["+57", "+52", "+1"];

/**
 * ClientModalV2 — minimal create-client dialog that posts to the v2
 * clients endpoint (POST /api/clients/v2/) used by suppliers-web.
 * Layout mirrors backoffice's supplier-commercial-user form
 * (MUI Grid + labeled TextField inputs).
 */
export const ClientModalV2: FC<{
  open: boolean;
  onClose: () => void;
  onCreated?: (client: ClientV2Response) => void;
}> = ({ open, onClose, onCreated }) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      type: "COMPANY",
      documentType: "NIT",
      phonePrefix: "+57",
    },
  });

  const type = useWatch({ control, name: "type" });

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setSaving(true);
    try {
      const payload = {
        type: values.type,
        firstName: values.firstName?.trim() || undefined,
        lastName: values.lastName?.trim() || undefined,
        companyName: values.companyName?.trim() || undefined,
        documentId: values.documentId?.trim() || undefined,
        documentType: values.documentType || undefined,
        email: values.email?.trim().toLowerCase() || undefined,
        phonePrefix: values.phonePrefix || undefined,
        phone: values.phone?.trim() || undefined,
        observations: values.observations?.trim() || undefined,
      };
      const res = await getSeeriApi().post<ClientV2Response>(
        "/api/clients/v2/",
        payload
      );
      notify("Cliente creado", { type: "success" });
      onCreated?.(res.data);
      reset();
      onClose();
    } catch (e) {
      const msg =
        e instanceof AxiosError ? e.response?.data?.message : String(e);
      notify(msg ?? "Error al crear cliente", { type: "error" });
    } finally {
      setSaving(false);
    }
  });

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="md"
      fullWidth
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>Nuevo cliente</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    label="Tipo de cliente"
                    fullWidth
                  >
                    <MenuItem value="COMPANY">Empresa</MenuItem>
                    <MenuItem value="PERSON">Persona</MenuItem>
                  </TextField>
                )}
              />
            </Grid>

            {type === "PERSON" ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register("firstName", {
                      required: "Nombres es requerido",
                    })}
                    size="small"
                    label="Nombres"
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register("lastName")}
                    size="small"
                    label="Apellidos"
                    fullWidth
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField
                  {...register("companyName", {
                    required: "Razón social es requerida",
                  })}
                  size="small"
                  label="Razón social"
                  error={!!errors.companyName}
                  helperText={errors.companyName?.message}
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12} sm={4}>
              <Controller
                control={control}
                name="documentType"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    label="Tipo de documento"
                    fullWidth
                  >
                    {DOC_TYPES.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                {...register("documentId")}
                size="small"
                label="Número de documento"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                {...register("email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Correo inválido",
                  },
                })}
                size="small"
                label="Correo"
                type="email"
                error={!!errors.email}
                helperText={errors.email?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <Controller
                control={control}
                name="phonePrefix"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    label="Prefijo"
                    fullWidth
                  >
                    {PHONE_PREFIXES.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField
                {...register("phone")}
                size="small"
                label="Teléfono"
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                {...register("observations")}
                size="small"
                label="Observaciones"
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={saving}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : null}
          >
            Crear cliente
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
